import { useMutation, useQuery } from "@apollo/client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Avatar } from "../../../../components/ui";
import {
  Table,
  multiSelect,
  SelectList,
} from "../../../../components/Functional";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../constants/listTypes";
import useUser from "../../../../hooks/useUser";

import {
  CONTACT_PAGINATION_QUERY_PARAM,
  getContactPaginationFromLocationSearch,
  getContactSearchFromLocationSearch,
  serializeContactSearchToQueryParams,
} from "../../components/useContactFilters";

import { formatCurrency } from "../../../../util";
import Actions, { getContactIdFromValues } from "./components/Actions";
import Header from "../../../../components/ui/ListHeader";
import searchedContacts from "./components/SearchedContacts";

import * as SelectListWorking from "./components/SelectListWorking";
import * as SelectListSaved from "./components/SelectListSaved";

const List = props => {
  const { formik } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { data: { lists: { edges = [] } = {} } = {}, loading: loadingWorkingList } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.CONTACT_WORKING },
    },
    fetchPolicy: "network-only",
  });
  const { user: { entityId } = {} } = useUser();

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: { input: { typeId: LIST_TYPES.CONTACT_WORKING } },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;

  // Ensure working list exists - create it if it doesn't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.CONTACT_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const { limit: paginationLimit, offset: paginationOffset } =
    getContactPaginationFromLocationSearch(location.search);
  const listIdParam = queryParams.get("listId");
  const listId = listIdParam ? +listIdParam : null;
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;
    
    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }
    
    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }
    
    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, status, listId]);

  const buildPaginationUrl = ({ offset, limit }) => {
    const currentParams = new URLSearchParams(location.search);
    const reservedParams = new URLSearchParams();
    ["status", "listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          reservedParams.set(key, value);
        }
      },
    );
    reservedParams.set("status", reservedParams.get("status") || "all");
    reservedParams.set(
      CONTACT_PAGINATION_QUERY_PARAM,
      `${offset},${limit ?? paginationLimit}`,
    );

    const contactSearchParams = serializeContactSearchToQueryParams({
      currentParams: reservedParams,
      searchInput: getContactSearchFromLocationSearch(location.search) || {},
    });
    return `/contacts?${contactSearchParams}`;
  };

  const handlePaginationChange = ({ offset, limit }) => {
    navigate(buildPaginationUrl({ offset, limit: limit ?? paginationLimit }));
  };

  const multiSelectProps = {
    formik,
    label: "Contacts",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    onSuccess: () => formik?.resetForm(),
    search: getContactSearchFromLocationSearch(location.search),
    workingListId,
  };

  const handleChange = value => {
    formik?.resetForm();
    let url = `/contacts?status=${value}&${CONTACT_PAGINATION_QUERY_PARAM}=0,${paginationLimit}`;
    
    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all", "my", and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }
    
    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list ID - use saved list ID if present, otherwise use working list ID
    const targetListId = listId && !isNaN(listId) ? listId : (workingListId || undefined);

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/contacts?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.CONTACT_WORKING,
    };

    return (
      <>
        <SelectList.CreateList {...createListProps} />
      </>
    );
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Contacts",
        name: "all",
        tableProps: searchedContacts(),
      },
      {
        actions: GeneralTabActions,
        label: "My Contacts",
        name: "my",
        tableProps: searchedContacts({
          salesUserId: { operator: "eq", value: entityId || -1 },
        }),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps(actionProps),
      },
    ],
  };

  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  // function fixes header.name for select lists when status = selectListWorking
  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          rowClass: "w-[74px] pr-0",
          label: "",
          name: "image",
          type: "custom",
          component: props => (
            <Avatar
              size="lg"
              src={props?.values?.img}
              label={props?.values?.img || props?.values?.name}
              onClick={() => {
                const contactId = getContactIdFromValues(props?.values);
                if (typeof contactId === "number") {
                  navigate(`/contacts/${contactId}/details`);
                }
              }}
            />
          ),
        },
        { label: "Type", name: "type.name", sortKey: "typeId", type: "label" },
        {
          label: ["Contact Name", "Email"],
          name: ["name", "email"],
          type: "multi-label",
          rowClass: "min-w-[320px] box-border",
          to: values => {
            const contactId = getContactIdFromValues(values);
            return typeof contactId === "number"
              ? `/contacts/${contactId}/details`
              : "";
          },
        },
        {
          label: "Company & Title",
          name:
            status === "selectListWorking"
              ? ["companyName", "title"]
              : ["company", "title"],
          type: "multi-label",
          rowClass: "!whitespace-normal",
        },
        {
          label: "Location",
          name: ["defaultAddress.county", "defaultAddress.country.name"],
          sortKey: "country",
          type: "multi-label",
        },
        {
          label: "Sales User",
          name: "salesUser.name",
          sortKey: "salesUserId",
          type: "label",
        },
        { 
          label: "Total Spend",
          name: "totalSpend",
          sortKey: "totalSpend",
          type: "custom",
          component: ({ values }) => {
            return <div>{formatCurrency(values?.totalSpend)}</div>;
          },
        },
        { label: "", type: "custom", component: Actions },
      ],
    }),
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
    limit: paginationLimit,
    variables: {
      ...(tabTableProps?.variables || {}),
      limit: paginationLimit,
      offset: paginationOffset,
    },
    pagination: {
      ...(tabTableProps?.pagination || {}),
      getPageHref: ({ offset, limit }) => buildPaginationUrl({ offset, limit }),
      onChange: handlePaginationChange,
    },
  };

  return (
    <>
        <Header
          leftText="All Contacts"
          rightComponent={() => {
            return (
              <div className="flex gap-4">{tabActions?.(actionProps)}</div>
            );
          }}
        />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
